import { StateContext } from "../../../../../state/stateContext";
import { OVERVIEW_EVENTS } from "@utils/enums";
import { useRef, useEffect, useContext } from "react";
import { useLanguage } from "@utils/hooks/useLanguage";
import { useTranslations } from "../../../../../translation/TranslationContext";

interface IGoalScorrerAndAssistProps {
    ml: string;
    mr: string;
    startPosition?: "start" | "end";
    event_type: string;
    player: string;
    assistant?: string;
    homeScore: string | number;
    awayScore: string | number;
    isHomeGoal: boolean;
}


export const MatchPlayerNameAndScore = ({ ml, mr, startPosition = 'end', event_type, player, assistant, homeScore, awayScore, isHomeGoal }: IGoalScorrerAndAssistProps) => {

    const playerNameAndScoreRef = useRef<HTMLDivElement>(null);
    const playerNameRef = useRef<HTMLSpanElement>(null);
    const playerScoreRef = useRef<HTMLSpanElement>(null);
    const eventNameContainerRef = useRef<HTMLDivElement>(null);
    const eventTextRef = useRef<HTMLSpanElement>(null);

    const ctx = useContext(StateContext);
    const { stContext } = ctx || {};
    const { overviewEventAndTimeWidth } = stContext || {};

    const language = useLanguage();
    const t = useTranslations(language);

    useEffect(() => {
        if (playerNameAndScoreRef.current && playerScoreRef.current && playerNameRef.current) {
            const containerWidth = playerNameAndScoreRef.current.offsetWidth;
            const scoreWidth = playerScoreRef.current.offsetWidth;
            const availableWidth = containerWidth - scoreWidth-10;
            playerNameRef.current.style.maxWidth = `${availableWidth}px`;
        }

        if (eventNameContainerRef.current && eventTextRef.current) {
            const containerWidth = eventNameContainerRef.current.offsetWidth-10;
            eventTextRef.current.style.maxWidth = `${containerWidth}px`;
        }
    }, [player, homeScore, awayScore, event_type, assistant]);

    const switchEventName = () => {
        switch (event_type) {
            case OVERVIEW_EVENTS.PENALTY_GOAL:
                return t[`InHouseStats.OverviewEventNamePenalty`]
            case OVERVIEW_EVENTS.PENALTIES_MISSED:
                return t[`InHouseStats.OverviewEventNamePenaltyMissed`]
            case OVERVIEW_EVENTS.PENALTIES_GOAL:
                return t[`InHouseStats.OverviewEventNamePenalty`]
            case OVERVIEW_EVENTS.PENALTIES_SAVED:
                return t[`InHouseStats.OverviewEventNamePenaltySaved`]
            case OVERVIEW_EVENTS.GOAL:
                return assistant ? `${t[`InHouseStats.OverviewEventNameGoalAssist`]} ${assistant}` : ""
            case OVERVIEW_EVENTS.PENALTY_SAVED:
                return t[`InHouseStats.OverviewEventNamePenaltySaved`]
            case OVERVIEW_EVENTS.YELLOW_CARD:
                return t[`InHouseStats.OverviewEventNameFoul`]
            case OVERVIEW_EVENTS.RED_CARD:
                return t[`InHouseStats.OverviewEventNameFoul`]
            case OVERVIEW_EVENTS.OFFSIDE:
                return t[`InHouseStats.OverviewEventNameOffside`]
            case OVERVIEW_EVENTS.SHOT_ON_THE_TARGET:
                return t[`InHouseStats.ShotsOnTarget`]
            case OVERVIEW_EVENTS.SHOT_OFF_THE_TARGET:
                return t[`InHouseStats.ShotsOffTarget`]
            case OVERVIEW_EVENTS.CORNER:
                return t[`InHouseStats.OverviewEventNameCorner`]
            case OVERVIEW_EVENTS.OWN_GOAL:
                return t[`InHouseStats.OverviewEventNameOwnGoal`]
            case OVERVIEW_EVENTS.PENALTY_MISSED:
                return t[`InHouseStats.OverviewEventNamePenaltyMissed`]
            case OVERVIEW_EVENTS.INJURY_BREAK:
                return t[`InHouseStats.OverviewEventNameInjuryBreak`]
            default:
                return `${player}`
        }
    }

    const switchPlayerName = () => {
        switch (isHomeGoal) {
            case true:
                return (
                    <div ref={playerNameAndScoreRef} className="w-full flex">
                        <span ref={playerScoreRef}>{`${homeScore}-${awayScore}\u00A0`}</span>
                        <span ref={playerNameRef} className="truncate">{player}</span>
                    </div>
                );
            case false:
                return (
                    <div ref={playerNameAndScoreRef} className="w-full flex justify-end">
                        <span ref={playerNameRef} className="truncate">{player}</span>
                        <span ref={playerScoreRef}>{`\u00A0${homeScore}-${awayScore}`}</span>
                    </div>
                );
            default:
                return `${player}`;
        }
    }

    return (
        <div 
            data-testid="overview-player-name-and-score"
            className={`flex flex-col justify-center truncate w-full`} 
            style={{ 
                marginLeft: ml, 
                marginRight: mr, 
                alignItems: "flex-" + startPosition
            }}>
            <div 
                data-testid="overview-player-name"
                className={`text-base120 dark:text-monoY text-[12px] leading-[16px] font-medium font-robotoMedium truncate flex w-full`}
                style={{ alignItems: "flex-" + startPosition }}>
                {switchPlayerName()}
            </div>
            <div 
                data-testid="overview-event-name"
                ref={eventNameContainerRef} 
                className={`text-base0 dark:text-base090 text-[12px] leading-[16px] font-normal font-robotoRegular flex w-full`}
                style={{ justifyContent: startPosition === 'start' ? 'flex-start' : 'flex-end' }}>
                <span ref={eventTextRef} className="truncate">
                    {switchEventName()}
                </span>
            </div>
        </div>
    )
}